// Trust Center document loader.
//
// Loads the document pack in docs/trust/*.md at build time so the SPA can serve
// it publicly at /trust/* — the URLs already referenced by
// src/lib/complianceEvidence.ts. The markdown files remain the single source of
// truth; nothing is duplicated.

const modules = import.meta.glob('/docs/trust/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

export interface TrustDoc {
  slug: string;
  title: string;
  content: string;
}

/** docs/trust/security-whitepaper.md -> "security-whitepaper" */
function slugFromPath(path: string): string {
  return path.split('/').pop()!.replace(/\.md$/, '');
}

function humanize(slug: string): string {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** First H1, falling back to a humanized slug. */
function titleFromMarkdown(md: string, fallback: string): string {
  const m = md.match(/^#\s+(.+)$/m);
  return m ? m[1].trim() : fallback;
}

/** Slug of the index document (docs/trust/README.md). */
export const TRUST_INDEX_SLUG = 'README';

const bySlug = new Map<string, TrustDoc>();
for (const [path, content] of Object.entries(modules)) {
  const slug = slugFromPath(path);
  bySlug.set(slug, {
    slug,
    title: titleFromMarkdown(content, humanize(slug)),
    content,
  });
}

export function getTrustDoc(slug: string): TrustDoc | undefined {
  return bySlug.get(slug);
}

export function hasTrustDoc(slug: string): boolean {
  return bySlug.has(slug);
}

export function getTrustIndex(): TrustDoc | undefined {
  return bySlug.get(TRUST_INDEX_SLUG);
}
